import axios from 'axios';

export const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Attach access token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Refresh token on 401
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !original._retry) {
      original._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const { data } = await axios.post('/api/auth/refresh', { refreshToken });
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/app/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }).then(r => r.data),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  me: () => api.get('/auth/me').then(r => r.data),
};

// Patients
export const patientsApi = {
  list: (params) => api.get('/patients', { params }).then(r => r.data),
  get: (id) => api.get(`/patients/${id}`).then(r => r.data),
  create: (data) => api.post('/patients', data).then(r => r.data),
  update: (id, data) => api.patch(`/patients/${id}`, data).then(r => r.data),
  consultations: (id) => api.get(`/patients/${id}/consultations`).then(r => r.data),
};

// Consultations
export const consultationsApi = {
  list: (params) => api.get('/consultations', { params }).then(r => r.data),
  get: (id) => api.get(`/consultations/${id}`).then(r => r.data),
  create: (data) => api.post('/consultations', data).then(r => r.data),
  addTranscript: (id, transcript) => api.post(`/consultations/${id}/transcript`, { transcript }).then(r => r.data),
  uploadAudio: (id, file, onProgress) => {
    const form = new FormData();
    form.append('audio', file);
    return api.post(`/consultations/${id}/audio`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }).then(r => r.data);
  },
  generateNote: (id, opts) => api.post(`/consultations/${id}/generate-note`, opts).then(r => r.data),
  notes: (id) => api.get(`/consultations/${id}/notes`).then(r => r.data),
};

// Notes
export const notesApi = {
  get: (id) => api.get(`/notes/${id}`).then(r => r.data),
  update: (id, data) => api.patch(`/notes/${id}`, data).then(r => r.data),
  finalise: (id) => api.post(`/notes/${id}/finalise`).then(r => r.data),
};

// Facilities
export const facilitiesApi = {
  me: () => api.get('/facilities/me').then(r => r.data),
  stats: () => api.get('/facilities/me/stats').then(r => r.data),
  users: () => api.get('/facilities/me/users').then(r => r.data),
  createUser: (data) => api.post('/facilities/me/users', data).then(r => r.data),
};
